// Route: /notifications/$id — детальный просмотр уведомления.
// Spec: docs/ui-spec/notifications.detail.{desktop,mobile}.md
//
// Тактильный редизайн «Картотека» (§8.V): карточка уведомления — панель e2,
// иконка типа — категорийная плитка; кнопка действия — тактильная клавиша.
//
// Источник данных — PowerSync stream `user_notifications`. Уведомление берётся
// из локальной SQLite через usePowerSyncQuery (реактивно), а не из MOCK_*.

import { PANEL_PAD } from "@studyhub/ui";
import { Badge } from "@studyhub/ui/components/badge";
import { Button } from "@studyhub/ui/components/button";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { CategoryTile } from "@studyhub/ui/components/studyhub/category-tile";
import { ElevatedPanel } from "@studyhub/ui/components/studyhub/elevated-panel";
import { IconArrowLeft, IconArrowRight, IconExternalLink } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FormattedDate } from "@/components/i18n/formatters";
import { parseArtifactKind } from "@/components/notifications/notification-adapter";
import { resolveNotificationVisual } from "@/components/notifications/notification-visuals";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useSession } from "@/hooks/use-session";
import { getPowerSync } from "@/lib/powersync/client";
import { useNotificationByIdQuery } from "@/lib/powersync/queries/use-notifications-query";

export const Route = createFileRoute("/notifications/$id")({
	component: NotificationDetailPage,
	pendingComponent: PagePendingFallback,
});

function NotificationDetailPage() {
	const { t } = useTranslation("notifications");
	const { id } = Route.useParams();
	const { user } = useSession();
	const navigate = useNavigate();
	const { data: rows, isLoading } = useNotificationByIdQuery(user?.id, id);
	const item = rows?.[0];

	// Авто-отметка прочитанным при открытии детали — через PowerSync local
	// UPDATE (offline-safe). Upload-queue пушит на сервер через
	// `/api/sync/powersync/write` → write-handler `notifications`. Идемпотентно —
	// ref-guard от повторов + WHERE read_at IS NULL.
	const markedRef = useRef(false);
	useEffect(() => {
		if (!item || markedRef.current || item.status === "read") return;
		markedRef.current = true;
		void getPowerSync().execute(
			`UPDATE notifications SET read_at = ? WHERE id = ? AND read_at IS NULL`,
			[new Date().toISOString(), id],
		);
	}, [item, id]);

	function onOpenArtifact() {
		const url = item?.action_url;
		if (!url) return;
		// Внешний URL → полная навигация; внутренний путь → SPA-навигация.
		if (/^https?:\/\//.test(url)) {
			window.location.href = url;
		} else {
			void navigate({ to: url });
		}
	}

	if (isLoading) {
		return (
			<div className="mx-auto w-full max-w-2xl px-4 py-6 lg:py-10">
				<Skeleton className="mb-4 h-9 w-32 rounded-[var(--radius-sm)]" />
				<Skeleton className="h-48 w-full rounded-[var(--radius-panel)]" />
			</div>
		);
	}

	if (!item) {
		return (
			<div className="mx-auto w-full max-w-2xl px-4 py-10">
				<ElevatedPanel elevation="e1" className="px-6 py-12 text-center">
					<p className="text-sm text-muted-foreground">{t("detail.notFound")}</p>
					<Button asChild variant="link" className="mt-2 cursor-pointer px-0">
						<Link to="/notifications">
							<IconArrowLeft size={14} /> {t("detail.back")}
						</Link>
					</Button>
				</ElevatedPanel>
			</div>
		);
	}

	return (
		<div className="mx-auto w-full max-w-2xl px-4 py-6 lg:py-10">
			<Button
				asChild
				variant="ghost"
				size="sm"
				className="-ml-2 mb-4 min-h-[44px] cursor-pointer lg:min-h-0"
			>
				<Link to="/notifications">
					<IconArrowLeft size={14} />
					{t("detail.back")}
				</Link>
			</Button>

			<ElevatedPanel className={PANEL_PAD}>
				<header className="flex items-start gap-3">
					{(() => {
						const { category, Icon } = resolveNotificationVisual({
							type: item.type,
							kind: parseArtifactKind(item.data),
						});
						return <CategoryTile category={category} icon={<Icon stroke={1.75} />} size="lg" />;
					})()}
					<div className="min-w-0 flex-1">
						<Badge variant="secondary" className="mb-2 text-2xs">
							{t(`categories.${item.type}`, { defaultValue: item.type })}
						</Badge>
						<h1 className="break-words text-xl font-semibold text-foreground">{item.title}</h1>
						<p className="mt-1 text-xs tabular-nums text-muted-foreground">
							{t("detail.createdAt")}:{" "}
							<FormattedDate value={item.created_at} format="long" withTime />
						</p>
					</div>
				</header>

				<div className="mt-5 space-y-3 text-sm leading-relaxed text-foreground">
					<p className="break-words">{item.message}</p>
				</div>

				{item.action_url && (
					<div className="mt-6 flex gap-2 border-t border-border pt-5">
						<Button className="cursor-pointer" onClick={onOpenArtifact}>
							<IconExternalLink size={14} />
							{t("detail.openArtifact")}
							<IconArrowRight size={14} />
						</Button>
					</div>
				)}
			</ElevatedPanel>
		</div>
	);
}
